import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Eye, EyeOff, Play, Save, Smile } from 'lucide-react';
import ModelViewer from '../components/ModelViewer';
import { ActionBtn, MoveButtons, moveItem } from '../components/ReorderControls';
import { fileUrl, textureMaps, modelStyle, saveExpressions, saveDisplay } from '../lib/api';
import { mergeExpressions } from '../lib/expressions';
import { useEscape } from '../hooks/useEscape';
import { MarkersSwitch } from './StyleEditor';

export default function ExpressionEditor({ it, onClose, onSaved }) {
  const [rows, setRows] = useState(null); // null = still scanning the file
  const [previewKey, setPreviewKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [nav, setNav] = useState(it.expr_nav !== false);
  const [markers, setMarkers] = useState(false);
  const [previewItem] = useState(() => ({ id: it.id, modelUrl: fileUrl(it.filename), textures: textureMaps(it.textures), ...modelStyle(it) }));
  useEscape(onClose);

  const onMorphNames = useCallback((names) => setRows((prev) => prev || mergeExpressions(it.expressions, names)), [it.expressions]);
  const patch = (i, p) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...p } : x)));

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await saveExpressions(it.id, rows.map((r) => ({ ...r, label: r.label.trim() })));
      if (nav !== (it.expr_nav !== false)) await saveDisplay(it.id, { expr_nav: nav });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail?.toString() || 'Save failed');
      setBusy(false);
    }
  };

  const target = document.getElementById('tv-screen-root') || document.body;
  return createPortal(
    <div className="absolute inset-0 z-[100] flex items-stretch sm:items-center justify-center bg-[#0a0502]/94 backdrop-blur-sm p-2 sm:p-5" onClick={onClose}>
      <div className="relative w-full max-w-5xl max-h-full flex flex-col rounded-lg border-2 border-[#3a2512] bg-[#1a0e05] lightbox-in"
           onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Expressions for ${it.title}`} data-testid="expression-editor">
        <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border-b border-[#3a2512]">
          <div className="min-w-0">
            <div className="font-osd text-[11px] text-[#f5a623] tracking-widest">// EXPRESSIONS</div>
            <div className="font-display text-lg sm:text-2xl text-[#ffe9b8] truncate">{it.title}</div>
          </div>
          <ActionBtn onClick={onClose} label="Close" testId="expression-editor-close"><X className="w-5 h-5" /></ActionBtn>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4">
          <div className="h-[200px] sm:h-[300px] sticky -top-3 sm:-top-4 z-10 rounded-lg overflow-hidden border-2 border-[#3a2512]" data-testid="expression-editor-preview">
            <ModelViewer item={previewItem} expanded fillContainer expression={previewKey} onMorphNames={onMorphNames} showLights={markers} />
            <MarkersSwitch on={markers} onChange={setMarkers} />
          </div>
          <div className="min-w-0 space-y-2">
            {rows === null && <Note>// SCANNING MODEL FOR BLENDSHAPES…</Note>}
            {rows?.length === 0 && <Note>NO EXPRESSIONS FOUND IN THIS FILE. EXPORT AS .GLB WITH SHAPE KEYS / MORPH TARGETS INCLUDED.</Note>}
            {rows?.length > 0 && (
              <label className="flex items-center gap-3 min-h-[44px] px-3 border-2 border-[#3a2512] rounded-lg bg-[#0f0704] cursor-pointer" data-testid="expression-nav-toggle-row">
                <input type="checkbox" checked={nav} onChange={(e) => setNav(e.target.checked)} className="w-5 h-5 accent-[#f5a623] shrink-0" data-testid="expression-nav-toggle" />
                <span className="font-osd text-xs tracking-wider text-[#ffe9b8]">SHOW ◀ ▶ ARROWS AND NAME BAR <span className="text-[#8b6a3e]">· IN THE EXPANDED VIEW</span></span>
              </label>
            )}
            {rows?.length > 0 && (
              <div className="font-osd text-[11px] text-[#8b6a3e] tracking-widest">
                // {rows.filter((r) => !r.hidden).length} OF {rows.length} VISIBLE · TAP ▶ TO PREVIEW
              </div>
            )}
            {rows?.map((r, i) => (
              <Row key={r.key} r={r} index={i} total={rows.length} previewing={previewKey === r.key}
                   onLabel={(label) => patch(i, { label })} onToggle={() => patch(i, { hidden: !r.hidden })}
                   onPreview={() => setPreviewKey(previewKey === r.key ? null : r.key)}
                   onMove={(idx, dir) => setRows((x) => moveItem(x, idx, dir))} />
            ))}
          </div>
        </div>

        <div className="p-3 sm:p-4 border-t border-[#3a2512] flex items-center justify-between gap-2 flex-wrap">
          <div className="font-osd text-xs text-[#ff6a3a]" role="alert" data-testid="expression-editor-error">{err}</div>
          <div className="flex gap-2 ml-auto">
            <ActionBtn onClick={onClose} label="Cancel" testId="expression-editor-cancel">CANCEL</ActionBtn>
            <ActionBtn onClick={save} disabled={busy || !rows?.length} label="Save expressions" tone="accent" testId="expression-editor-save">
              <Save className="w-4 h-4" /> {busy ? 'SAVING…' : 'SAVE'}
            </ActionBtn>
          </div>
        </div>
      </div>
    </div>,
    target
  );
}

function Row({ r, index, total, previewing, onLabel, onToggle, onPreview, onMove }) {
  return (
    <div className={`p-2.5 border-2 rounded-lg bg-[#0f0704] transition-opacity ${previewing ? 'border-[#9dff7a]/60' : 'border-[#3a2512]'} ${r.hidden ? 'opacity-55' : ''}`}
         data-testid={`expression-row-${index}`}>
      <div className="flex items-center gap-2">
        <Smile className="w-4 h-4 text-[#f5a623] shrink-0" aria-hidden />
        <input value={r.label} onChange={(e) => onLabel(e.target.value)} maxLength={80} aria-label={`Display name for ${r.key}`}
               className="flex-1 min-w-0 bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] font-body text-sm px-2.5 py-2 rounded"
               data-testid={`expression-label-${index}`} />
      </div>
      <div className="font-osd text-[10px] text-[#8b6a3e] tracking-wider mt-1 truncate" title={r.key}>FILE: {r.key}{r.hidden ? ' · HIDDEN' : ''}</div>
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <MoveButtons index={index} total={total} onMove={onMove} label={r.label}
                     upTestId={`expression-up-${index}`} downTestId={`expression-down-${index}`} />
        <div className="flex gap-1.5">
          <ActionBtn onClick={onPreview} label={previewing ? 'Back to neutral' : `Preview ${r.label}`} testId={`expression-preview-${index}`} tone={previewing ? 'accent' : 'default'}>
            <Play className="w-4 h-4" />
          </ActionBtn>
          <ActionBtn onClick={onToggle} label={r.hidden ? `Show ${r.label}` : `Hide ${r.label}`} testId={`expression-hide-${index}`}>
            {r.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </ActionBtn>
        </div>
      </div>
    </div>
  );
}

function Note({ children }) {
  return <div className="font-osd text-xs text-[#8b6a3e] border-2 border-dashed border-[#5a3418] px-4 py-8 text-center rounded" data-testid="expression-editor-note">{children}</div>;
}
